// Settings.js
import React, { useState, useEffect } from "react";
import "./Settings.css";
import api from '../../utils/axios';
import { MdFeedback, MdSync, MdPerson, MdSecurity, MdReceipt } from 'react-icons/md';
// import { FaUsers } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import NotificationBadge from '../../components/NotificationBadge';

const categories = ['Academic', 'AgriTech', 'BioTech', 'EdTech', 'FinTech', 'General', 'HardTech', 'HealthTech'];

const TABS = {
  PROFILE: 'profile',
  // MANAGE_NETWORK: 'network',
  SECURITY: 'security',
  BILLING: 'billing',
  FEEDBACK: 'feedback'
};

const packages = [
  { id: 1, title: 'Focus 15: Quick Presentation Tuner' },
  { id: 2, title: 'Half-Hour Impact Builder' },
  { id: 3, title: 'Power Hour: Comprehensive Coaching' },
];

const getPackageTitle = (packageId) => {
  const pkg = packages.find(p => p.id === packageId);
  return pkg ? pkg.title : 'Unknown Package';
};

const Settings = () => {
  useEffect(() => {
    document.title = "SwavAI - Settings";
  }, []);

  const [profilePicture, setProfilePicture] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.PROFILE);
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [formData, setFormData] = useState({
    displayName: '',
    linkedinUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    tiktokUrl: '',
    twitterUrl: '',
    companyName: '',
    companySector: '',
    personalWebsite: '',
    companyWebsite: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [cvUrl, setCvUrl] = useState(null);

  const fetchConnections = async () => {
    try {
      const response = await api.get('/user/connections');
      setConnections(response.data);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/user/profile');
        const data = response.data;
        setDisplayName(data.display_name || '');
        setProfilePicture(data.profile_picture);
        setFormData({
          displayName: data.display_name || '',
          linkedinUrl: data.linkedin_url || '',
          facebookUrl: data.facebook_url || '',
          instagramUrl: data.instagram_url || '',
          tiktokUrl: data.tiktok_url || '',
          twitterUrl: data.twitter_url || '',
          companyName: data.company_name || '',
          companySector: data.company_sector || '',
          personalWebsite: data.personal_website || '',
          companyWebsite: data.company_website || '',
        });
        setCvUrl(response.data.cv_url);
      } catch (err) {
        setError('Failed to load profile');
      }
    };
    fetchProfile();
    fetchPendingRequests(); // Add this line to fetch pending requests on component mount
    fetchConnections();
  }, []);

  useEffect(() => {
    if (activeTab === TABS.BILLING) {
      fetchTransactions();
    }
  }, [activeTab]);

  const fetchTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await api.get('/upgrade/history');
      setTransactions(response.data.transactions || []);
    } catch (error) {
      setError('Failed to load transaction history');
      console.error('Error fetching transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    if (activeTab === TABS.MANAGE_NETWORK) {
      fetchPendingRequests();
    }
  }, [activeTab]);

  const fetchPendingRequests = async () => {
    try {
      const response = await api.get('/user/connections/pending');
      setPendingRequests(response.data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const handleConnectionResponse = async (requesterId, accept) => {
    try {
      await api.post('/user/connections/respond', { requesterId, accept });
      fetchPendingRequests();
      fetchConnections();
    } catch (error) {
      console.error('Error responding to connection:', error);
      setError('Failed to respond to connection request');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formDataObj = new FormData();
      if (e.target.photo.files[0]) {
        formDataObj.append('profilePicture', e.target.photo.files[0]);
      }
      if (cvFile) {
        formDataObj.append('cv', cvFile);
      }

      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataObj.append(key, formData[key]);
        }
      });

      await api.put('/user/profile', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfilePicture(URL.createObjectURL(file));
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (!feedback.trim()) {
        throw new Error('Please enter your feedback');
      }

      await api.post('/user/feedback', {
        feedback,
        feedbackType
      });

      setSuccess('Thank you for your feedback! We greatly appreciate your input to help improve SwavAI.');
      setFeedback('');
      setFeedbackType('general');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCvDelete = async () => {
    if (window.confirm('Are you sure you want to delete your CV?')) {
      try {
        await api.delete('/user/profile/cv');
        setCvUrl(null);
        setSuccess('CV deleted successfully');
      } catch (error) {
        setError('Failed to delete CV');
      }
    }
  };

  // Define NetworkTabContent component
  const NetworkTabContent = ({ connections, pendingRequests, handleConnectionResponse }) => (
    <div className="tab-content">
      <h2>Network Management</h2>
      {pendingRequests.length > 0 && (
        <div className="pending-requests-section">
          <h3>Pending Requests ({pendingRequests.length})</h3>
          <div className="pending-requests">
            {pendingRequests.map(request => (
              <div key={request.id} className="request-item">
                <Link to={`/users/${request.id}`} className="user-info">
                  <img src={request.profile_picture || "https://via.placeholder.com/40"}
                    alt={request.display_name} />
                  <span>{request.display_name}</span>
                </Link>
                <div className="request-actions">
                  <button className="accept-btn"
                    onClick={() => handleConnectionResponse(request.id, true)}>
                    Accept
                  </button>
                  <button className="decline-btn"
                    onClick={() => handleConnectionResponse(request.id, false)}>
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="connections-section">
        <h3>Current Connections</h3>
        {connections.length > 0 ? (
          <div className="connections-list">
            {connections.map(connection => (
              <Link
                to={`/users/${connection.id}`}
                key={connection.id}
                className="connection-item">
                <img
                  src={connection.profile_picture || "https://via.placeholder.com/48"}
                  alt={connection.display_name} />
                <span>{connection.display_name}</span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="no-connections">
            <p>You don't have any connections yet.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case TABS.PROFILE:
        return (
          <div className="tab-content">
            <h2>Profile Settings</h2>
            <form onSubmit={handleProfileUpdate}>
              <div className="profile-picture-container">
                <div className="profile-picture">
                  <img src={profilePicture || "https://via.placeholder.com/150"} alt="Profile" />
                  <div className="profile-picture-overlay">
                    <label className="upload-button" htmlFor="photo">Change Photo</label>
                  </div>
                </div>
                <input id="photo" name="photo" type="file" accept="image/*" onChange={handleProfilePictureChange} hidden />
              </div>

              <div className="form-sections">
                <div className="form-section">
                  <h3>Basic Information</h3>
                  <div className="form-group">
                    <label htmlFor="displayName">Display Name</label>
                    <input type="text" id="displayName" value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Company Information</label>
                    <input type="text" placeholder="Company Name" value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} />
                    <select value={formData.companySector}
                      onChange={(e) => setFormData({ ...formData, companySector: e.target.value })}>
                      <option value="">Select Sector</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Social Links</h3>
                  <div className="social-inputs">
                    {[
                      { name: 'linkedinUrl', icon: 'LinkedIn' },
                      { name: 'facebookUrl', icon: 'Facebook' },
                      { name: 'instagramUrl', icon: 'Instagram' },
                      { name: 'tiktokUrl', icon: 'TikTok' },
                      { name: 'twitterUrl', icon: 'Twitter' }
                    ].map(social => (
                      <div className="form-group" key={social.name}>
                        <label>{social.icon}</label>
                        <input type="url" value={formData[social.name]}
                          onChange={(e) => setFormData({ ...formData, [social.name]: e.target.value })} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-section">
                  <h3>Websites & Documents</h3>
                  <div className="form-group">
                    <input type="url" placeholder="Personal Website" value={formData.personalWebsite}
                      onChange={(e) => setFormData({ ...formData, personalWebsite: e.target.value })} />
                    <input type="url" placeholder="Company Website" value={formData.companyWebsite}
                      onChange={(e) => setFormData({ ...formData, companyWebsite: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>CV</label>
                    {cvUrl ? (
                      <div className="cv-actions">
                        <a href={cvUrl} target="_blank" rel="noopener noreferrer" className="cv-download">
                          Download CV
                        </a>
                        <button type="button" onClick={handleCvDelete} className="cv-delete">
                          Delete CV
                        </button>
                      </div>
                    ) : (
                      <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCvFile(e.target.files[0])} />
                    )}
                  </div>
                </div>
              </div>

              <button type="submit" className="save-button" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        );

      /*
      case TABS.MANAGE_NETWORK:
        return (
          <NetworkTabContent
            connections={connections}
            pendingRequests={pendingRequests}
            handleConnectionResponse={handleConnectionResponse}
          />
        );
      */
      case TABS.SECURITY:
        return (
          <div className="tab-content">
            <h2>Security Settings</h2>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input type="email" id="email" placeholder="Enter your email" />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input type="password" id="password" placeholder="Enter new password" />
            </div>
            <button className="save-button">Update Security Settings</button>
          </div>
        );

      case TABS.BILLING:
        return (
          <div className="tab-content">
            <h2>Transaction History</h2>
            {loadingTransactions ? (
              <div className="loading-spinner" />
            ) : transactions.length > 0 ? (
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Package</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{new Date(transaction.purchase_date).toLocaleDateString()}</td>
                      <td>{getPackageTitle(transaction.package_id)}</td>
                      <td>${Number(transaction.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">No transactions found</div>
            )}
          </div>
        );

      case TABS.FEEDBACK:
        return (
          <div className="tab-content">
            <h2>Send Feedback</h2>
            <form onSubmit={handleFeedbackSubmit} className="feedback-form">
              <div className="form-group">
                <label htmlFor="feedback-type">Type of Feedback</label>
                <select id="feedback-type" value={feedbackType} onChange={(e) => setFeedbackType(e.target.value)}>
                  <option value="general">General Feedback</option>
                  <option value="bug">Report a Bug</option>
                  <option value="feature">Request a Feature</option>
                  <option value="improvement">Suggest an Improvement</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="feedback">Your Message</label>
                <textarea id="feedback" value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={5} />
              </div>
              <button type="submit" className="submit-button" disabled={isSubmitting || !feedback.trim()}>
                {isSubmitting ? <span><MdSync className="spin" /> Submitting...</span> : 'Submit Feedback'}
              </button>
            </form>
          </div>
        );
    }
  };

  return (
    <div className="settings">
      <nav className={`settings-tabs ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <button
          className="mobile-menu-button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          <span className="mobile-arrow">{isMobileMenuOpen ? '▼' : '▲'}</span>
        </button>

        <div className="tabs-container">
          {[
            { id: TABS.PROFILE, icon: <MdPerson />, label: 'Profile' },
            /*
            {
              id: TABS.MANAGE_NETWORK,
              icon: <FaUsers />,
              label: 'Network',
              hasBadge: true
            },
            */
            { id: TABS.SECURITY, icon: <MdSecurity />, label: 'Security' },
            { id: TABS.BILLING, icon: <MdReceipt />, label: 'Billing' },
            { id: TABS.FEEDBACK, icon: <MdFeedback />, label: 'Feedback' }
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                setIsMobileMenuOpen(false);
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.hasBadge && pendingRequests.length > 0 && (
                <NotificationBadge count={pendingRequests.length} />
              )}
            </button>
          ))}
        </div>
      </nav>

      <div className="settings-content">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        {renderTabContent()}
      </div>
    </div>
  );
};

export default Settings;